﻿import { useCallback, useEffect, useMemo } from 'react';

import { useIsDesktop } from '../../../hooks/useIsDesktop';
import {
  AlertaWebTO,
  AppNotification,
  NotificationType,
} from '../../../hooks/useNotifications.types';

import useNotificationsAtom from '@/hooks/useNotificationsAtom';
import ROUTES from '@/router/routes';
import { getAlertasListado, marcarAlertasLeidas } from '@/services/alertasWeb';

export type {
  NotificationType,
  AppNotification,
} from '../../../hooks/useNotifications.types';

const mapAgrupacionToType = (
  agrupacion?: number | string
): NotificationType => {
  switch (String(agrupacion)) {
    case '1':
      return 'generic';
    case '3':
      return 'tda_invite';
    case '4':
      return 'ml_invite';
    case '5':
      return 'desafio_pending';
    case '6':
      return 'pin_gift';
    case '7':
      return 'diploma';
    default:
      return 'generic';
  }
};

const diplomaNotification = (list: AppNotification[]): AppNotification[] => {
  const idsDiploma = list.filter((n) => n.raw?.idTipoAlerta === 17);
  if (idsDiploma.length === 0) return list;

  const diplomasAvailables =
    'Tenés un diploma por tu rendimiento. Podés verlo acá.';

  const grouped: AppNotification = {
    ...idsDiploma[0],
    id: 'group-alert-id-17',
    type: 'generic',
    title: 'Último momento',
    message: diplomasAvailables,
    raw: {
      ...idsDiploma[0].raw,
      mensaje: diplomasAvailables,
      mensajeMobile: diplomasAvailables,
      elink: {
        linkResp: ROUTES.editAccount.myAwards,
      },
    },
    linkUrl: ROUTES.editAccount.myAwards,
    isRead: idsDiploma.every((n) => n.isRead),
    date: idsDiploma.reduce(
      (acc, n) => (n.date > acc ? n.date : acc),
      idsDiploma[0].date
    ),
    groupedRaw: idsDiploma.map((n) => n.raw as AlertaWebTO),
  };

  let inserted = false;
  return list.reduce<AppNotification[]>((acc, n) => {
    if (n.raw?.idTipoAlerta === 17) {
      if (!inserted) {
        acc.push(grouped);
        inserted = true;
      }
    } else {
      acc.push(n);
    }
    return acc;
  }, []);
};

const getNotificationId = (alerta: AlertaWebTO) => {
  return String(
    alerta.id ??
      alerta.idConfiguracion ??
      `${alerta.fechaInicio2 || alerta.fechaInicioStr || Date.now()}-${
        alerta.titulo || alerta.agrupacionEnView_titulo || 'notification'
      }`
  );
};

const mapAlertaToAppNotification = (alerta: AlertaWebTO): AppNotification => {
  return {
    id: getNotificationId(alerta),
    type: mapAgrupacionToType(alerta.agrupacionEnView),
    title: String(
      alerta.agrupacionEnView_titulo || alerta.titulo || 'Notificación'
    ),
    message: alerta.mensajeMobile || alerta.mensaje || '',
    isRead: Boolean(alerta.leida2 || alerta.leida),
    date:
      alerta.fechaInicio2 || alerta.fechaInicioStr || new Date().toISOString(),
    linkUrl: alerta.elink?.linkResp,
    raw: alerta,
  };
};

export const useNotifications = () => {
  const { isMobile } = useIsDesktop();
  const { notificationsData, updateNotifications, setNotificationsData } =
    useNotificationsAtom();

  const { notifications, isLoading, initialized, showRead } = notificationsData;

  const fetchNotifications = useCallback(async () => {
    if (initialized) return;

    setNotificationsData((prev) => ({ ...prev, isLoading: true }));

    try {
      const resp = await getAlertasListado(isMobile ? 'mobile' : 'desktop');

      const mappedNotifications = Array.isArray(resp)
        ? diplomaNotification(resp.map(mapAlertaToAppNotification))
        : [];

      setNotificationsData((prev) => ({
        ...prev,
        notifications: mappedNotifications,
        isLoading: false,
        initialized: true,
      }));
    } catch (e) {
      console.error('Failed to fetch notifications', e);

      setNotificationsData((prev) => ({
        ...prev,
        isLoading: false,
        initialized: true,
      }));
    }
  }, [initialized, isMobile, setNotificationsData]);

  const markAsRead = useCallback(
    async (alertsToMark?: AppNotification[]) => {
      const source = alertsToMark ?? notifications;
      const unread = source.filter((a) => !a.isRead);

      if (!unread.length) return;

      const idsAlertas = unread
        .flatMap((a) => {
          const raws = a.groupedRaw ?? [a.raw as AlertaWebTO];
          return raws.map((raw) => {
            const id = raw.id || -1;
            const requiereDb = raw.requiereDb ? 'true' : 'false';
            const idConfig = raw.idConfiguracion || -1;
            return `${id},${requiereDb},${idConfig}`;
          });
        })
        .join('|');

      try {
        const resp = await marcarAlertasLeidas(idsAlertas);

        if (resp?.estado) {
          setNotificationsData((prev) => ({
            ...prev,
            notifications: prev.notifications.map((notification) =>
              unread.some(
                (unreadNotification) =>
                  unreadNotification.id === notification.id
              )
                ? { ...notification, isRead: true }
                : notification
            ),
          }));
        }
      } catch (e) {
        console.error('Failed to mark as read', e);
      }
    },
    [notifications, setNotificationsData]
  );

  const setShowRead = useCallback(
    (value: boolean) => {
      updateNotifications({ showRead: value });
    },
    [updateNotifications]
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const unreadNotifications = useMemo(
    () => notifications.filter((notification) => !notification.isRead),
    [notifications]
  );

  const readNotifications = useMemo(
    () => notifications.filter((notification) => notification.isRead),
    [notifications]
  );

  return {
    notifications,
    unreadNotifications,
    readNotifications,
    unreadCount: unreadNotifications.length,
    readCount: readNotifications.length,
    isLoading,
    showRead,
    fetchNotifications,
    refetch: fetchNotifications,
    markAsRead,
    setShowRead,
  };
};
